// Feature flags configuration
// Each feature has an id, name, description, and optional default state

export interface FeatureFlag {
  id: string
  name: string
  description: string
  defaultEnabled: boolean // Platform default if not set
}

// Master list of all available features
export const FEATURE_FLAGS: FeatureFlag[] = [
  { 
    id: 'recommendation-engine', 
    name: 'Recommendation Engine', 
    description: 'Enable AI-powered product recommendations',
    defaultEnabled: false
  },
  { 
    id: 'signals', 
    name: 'Signals', 
    description: 'Event tracking and analytics',
    defaultEnabled: true
  },
  { 
    id: 'analytics', 
    name: 'Analytics', 
    description: 'Advanced analytics dashboard',
    defaultEnabled: true
  },
  { 
    id: 'api-keys', 
    name: 'API Keys', 
    description: 'Allow API key generation',
    defaultEnabled: true
  },
  { 
    id: 'custom-models', 
    name: 'Custom Models', 
    description: 'Allow custom ML model configuration',
    defaultEnabled: false
  },
]

// Types for organization feature overrides
// null = Auto (use platform default)
// true = Enabled
// false = Disabled
export type FeatureOverride = boolean | null

export interface FeatureOverrides {
  [featureId: string]: FeatureOverride
}

// Platform settings document structure
export interface PlatformSettings {
  featureDefaults: {
    [featureId: string]: boolean
  }
  updatedAt?: Date
  updatedBy?: string
}

// Helper to get effective feature state
export function isFeatureEnabled(
  featureId: string,
  orgOverrides: FeatureOverrides | undefined,
  platformDefaults: { [featureId: string]: boolean } | undefined
): boolean {
  // Check org-level override first
  const orgOverride = orgOverrides?.[featureId]
  if (orgOverride !== null && orgOverride !== undefined) {
    return orgOverride
  }
  
  // Check platform defaults
  const platformDefault = platformDefaults?.[featureId]
  if (platformDefault !== undefined) {
    return platformDefault
  }
  
  // Fall back to hardcoded defaults
  const feature = FEATURE_FLAGS.find(f => f.id === featureId)
  return feature?.defaultEnabled ?? false
}

// Helper to get all enabled features for an organization
export function getEnabledFeatures(
  orgOverrides: FeatureOverrides | undefined,
  platformDefaults: { [featureId: string]: boolean } | undefined
): string[] {
  return FEATURE_FLAGS
    .filter(feature => isFeatureEnabled(feature.id, orgOverrides, platformDefaults))
    .map(feature => feature.id)
}

// Get the display state for a feature override
export function getFeatureDisplayState(
  featureId: string,
  orgOverrides: FeatureOverrides | undefined
): 'auto' | 'enabled' | 'disabled' {
  const override = orgOverrides?.[featureId]
  if (override === null || override === undefined) {
    return 'auto'
  }
  return override ? 'enabled' : 'disabled'
}

// Get the effective value when "auto" is selected
export function getAutoValue(
  featureId: string,
  platformDefaults?: { [featureId: string]: boolean }
): boolean {
  const platformDefault = platformDefaults?.[featureId]
  if (platformDefault !== undefined) {
    return platformDefault
  }
  const feature = FEATURE_FLAGS.find(f => f.id === featureId)
  return feature?.defaultEnabled ?? false
}
